import React from "react"
import { graphql } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"

import Layout from "../../components/layout"
import SEOComponent from "../../components/seo"
import H1 from "../../components/h1"
import FeaturedContent from "../../components/featuredContent"
import FeaturedLinkLayout from "../../components/featuredLinkLayout"
import HubSpotForm from "../../components/forms/hubspotForm"

export const query = graphql`
  query PersonalWhiskeyPageQuery {
    file(name: { eq: "customer-support" }) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH)
      }
    }
  }
`

const PersonalizedWhiskeyPage = ({ data, location }) => (
  <Layout>
    <SEOComponent
      title="Personalized Whiskey Bottles"
      description="Personalized and Engraved Liquor Bottles transform an ordinary bottle of alcohol into a memorable keepsake gift. Engrave and send personalized whiskey bottles."
    />
    <H1 header="Personalized Whiskey Bottles" />
    <section className="container px-5">
      <div className="columns">
        <div className="column">
          <StaticImage
            src="../../images/personalized-whiskey-bottles/custom-glenlivet-groomsman.jpg"
            alt="Personalized Whiskey Bottle"
          />
        </div>
        <div className="column">
          <StaticImage
            src="../../images/personalized-whiskey-bottles/johnnie-walker-blue-houston-astros.jpg"
            alt="Personalized Whiskey Bottle"
          />
        </div>
        <div className="column">
          <StaticImage
            src="../../images/personalized-whiskey-bottles/custom-jameson-wells.jpg"
            alt="Personalized Whiskey Bottle"
          />
        </div>
      </div>
    </section>
    <FeaturedContent
      header="Personalized Whiskey Bottles"
      textAlignment="center"
    >
      <p className="block is-size-5 is-size-6-mobile">
        Take the traditional whiskey bottle and turn it into a custom etched
        creation at Etching Expressions. Looking for personalized whiskey
        bottles for your kitchen or at-home bar? Work with our team of talented
        artists to create unique engraved liquor bottles, wine bottles, olive
        oil bottles, and more. Choose from a variety of ready-to-order designs
        or upload your own design ideas for a custom bottle you won’t find
        anywhere else. No matter the message or image, our personalized whiskey
        bottles make for a gorgeous addition to any bar or kitchen display.
      </p>
      <p className="block is-size-5 is-size-6-mobile">
        Our custom engraving process utilizes the latest etching technology; the
        bottle of your choice is engraved with sand blasting technology designed
        to maintain the quality of its contents. After the glass has been
        etched, our skilled artisans hand paint the imagery and text with the
        colors of your choice for a wholly personalized whiskey gift. From
        monograms to artwork, you’ll find a variety of ways to transform a
        traditional bottle into a unique present perfect for any special
        occasion.
      </p>
    </FeaturedContent>
    <section className="container p-5">
      <div className="columns is-centered">
        <div className="column is-two-thirds">
          <HubSpotForm
            bgColor="has-background-grey-lighter"
            formName="Liquor Page Form"
            pageName="Personalized Whiskey Bottles"
            location={location}
            formTitle="Fill in the form below"
            formSubtitle="We'll get back to you by the next business day"
          />
        </div>
      </div>
    </section>
    <FeaturedContent header="Top Quality Gifts" textAlignment="center">
      <p className="block is-size-5 is-size-6-mobile">
        Etching Expressions provides custom bottle engraving for wine bottles,
        champagne bottles, whiskey bottles, vodka bottles, and more. Let us help
        you personalize a custom etched bottle with your own message, logo,
        artwork or photo. Our personalized whiskey bottles allow for the utmost
        in customization; whether you’d like to send in a special message or
        image or choose from our ready-to-order designs, our engraving
        professionals can help you come up with the picture perfect bottle, fit
        for any special occasion.
      </p>
    </FeaturedContent>
    <section className="container p-5">
      <FeaturedLinkLayout
        image={data.file.childImageSharp.gatsbyImageData}
        alt="Customer support"
        heading="Want to talk about it first?"
        textAlignment="center"
      >
        <p className="block is-size-5 is-size-6-mobile">
          Call us at <b>866-944-3824</b>
        </p>
        <p className="block is-size-5 is-size-6-mobile">
          Gifting doesn't get better than this - our products are beautiful,
          customer service is unmatched and process is easy.
        </p>
        <p className="block is-size-5 is-size-6-mobile">
          Send elegant, thoughtful personalized gifts without the hassle.
        </p>
      </FeaturedLinkLayout>
    </section>
    <FeaturedContent header="Personalized Whiskey Bottle">
      <p className="block is-size-5 is-size-6-mobile">
        Looking for the perfect gift for that whiskey lover? Put your own spin
        on a traditional gift with a custom bottle of the recipient’s favorite
        whiskey flavors. Our skilled artisans can custom engrave the message or
        image of your choice right onto the glass to make for personalized
        whiskey bottles you won’t find anywhere else. Looking for something easy
        on your budget but sure to delight? We can craft personalized bottle
        labels to apply on the bottle of your choice.
      </p>
      <p className="block is-size-5 is-size-6-mobile">
        If whiskey’s not their drink of choice, peruse our assortment of liquor
        and wine bottles sure to fit every taste. From cabernet sauvignon to
        pinot noir, champagne to sparkling grape juice, you’re guaranteed to
        find something they’ll love. Browse our olive oil sets and find a
        decadent gift for the foodie extraordinaire, then have it personalized
        with a custom engraving or bottle label.
      </p>
      <h3 className="is-size-4 is-size-5-mobile has-text-danger is-uppercase">
        Perfect Gifts for Any Special Occasion
      </h3>
      <p className="block is-size-5 is-size-6-mobile">
        Celebrate any special occasion with beautiful engraved liquor and wine
        bottles. Whether you’re looking for personalized whiskey bottles for
        your dad on Father’s Day or need something special for your sweetheart
        on your upcoming anniversary, a whiskey gift from Etching Expressions is
        sure to be a hit. Simply send in the recipient’s favorite bottle of
        liquor, and our team will engrave your custom message. Choose from our
        liquor bottle offerings or send in a special request; we’ll work
        diligently to ensure you have the picture perfect personalized whiskey
        bottles for anyone on your list, no matter their tastes.
      </p>
      <h3 className="is-size-4 is-size-5-mobile has-text-danger is-uppercase">
        Corporate Gifts
      </h3>
      <p className="block is-size-5 is-size-6-mobile">
        Turn a whiskey gift into the perfect branding opportunity; adhere a
        custom bottle label to the whiskey, wine, or beer of your choice that’s
        emblazoned with your company logo. Our artists work diligently to
        provide perfect color matching to ensure your personalized whiskey or
        wine bottles serve as the quintessential promotional item.
      </p>
      <p className="block is-size-5 is-size-6-mobile">
        If you’re on the hunt for personalized whiskey bottles, contact us
        today, toll-free at 1-866-944-3824.
      </p>
    </FeaturedContent>
  </Layout>
)

export default PersonalizedWhiskeyPage
